import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Slider,
  Snackbar,
  Stack,
  Switch,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { io, Socket } from 'socket.io-client';
import api, { getAdminUserCustomFrameAssets } from '../../services/api';
import { SOCKET_URL } from '../../config/api';
import { useI18n } from '../../i18n/useI18n';
import customFrameService from '../../services/customFrameService';
import { userFrameService } from '../../services/userFrameService';
import type { CustomFrame } from '../../types';
import CustomFrameLayoutPreview from './CustomFrameLayoutPreview';
import { rewriteToCdn } from '../../utils/cdn';

const PINK = '#e86a8a';

const LAYOUTS = [
  { key: '2:1', w: 397, h: 197 },
  { key: '1:2', w: 202, h: 402 },
  { key: '6:2', w: 598, h: 201 },
  { key: '2:6', w: 201, h: 598 },
  { key: '4:6', w: 402, h: 598 },
  { key: '3:4', w: 297, h: 402 },
  { key: '4:3', w: 402, h: 297 },
  { key: '6:4', w: 598, h: 402 },
] as const;

const LAYOUT_DEFAULTS: Record<string, QrPos> = {
  '2:1': { layout: '2:1', baseX: 369, baseY: 167, size: 30, offsetX: 0, offsetY: 0, updatedAt: null },
  '1:2': { layout: '1:2', baseX: 172, baseY: 374, size: 30, offsetX: 0, offsetY: 0, updatedAt: null },
  '6:2': { layout: '6:2', baseX: 568, baseY: 171, size: 30, offsetX: 0, offsetY: 0, updatedAt: null },
  '2:6': { layout: '2:6', baseX: 171, baseY: 568, size: 30, offsetX: 0, offsetY: 0, updatedAt: null },
  '4:6': { layout: '4:6', baseX: 372, baseY: 568, size: 30, offsetX: 0, offsetY: 0, updatedAt: null },
  '3:4': { layout: '3:4', baseX: 267, baseY: 372, size: 30, offsetX: 0, offsetY: 0, updatedAt: null },
  '6:4': { layout: '6:4', baseX: 568, baseY: 372, size: 30, offsetX: 0, offsetY: 0, updatedAt: null },
  '4:3': { layout: '4:3', baseX: 372, baseY: 267, size: 30, offsetX: 0, offsetY: 0, updatedAt: null },
};

const SIZE_TO_LAYOUT: Record<string, string> = {
  '2x4': '1:2',
  '4x2': '2:1',
  '2x6': '2:6',
  '6x2': '6:2',
  '4x6': '4:6',
  '6x4': '6:4',
  '3x4': '3:4',
  '4x3': '4:3',
};

type QrPos = {
  layout: string;
  baseX: number;
  baseY: number;
  size: number;
  offsetX: number;
  offsetY: number;
  updatedAt?: string | Date | null;
};

type PreviewUserFrame = {
  _id: string;
  name: string;
  url: string;
};

type MachineQrPositionEditorProps = {
  userId?: string;
  machineCode: string;
};

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function clampSize(size: number) {
  return clamp(Math.round(size) || 30, 30, 60);
}

function clampOffset(offset: number, base: number, size: number) {
  const sizeAdj = clampSize(size) - 30;
  return clamp(offset, -base, -sizeAdj);
}

function frameKey(id: string) {
  return `frame:${id}`;
}

function layoutForFrame(frame: CustomFrame) {
  return SIZE_TO_LAYOUT[String(frame.size || '').toLowerCase()] || '';
}

const nudgeBtnSx = {
  width: 34,
  height: 34,
  border: `1.5px solid ${alpha(PINK, 0.45)}`,
  color: PINK,
  bgcolor: '#fff',
  '&:hover': { bgcolor: alpha(PINK, 0.08), borderColor: PINK },
};

const MachineQrPositionEditor: React.FC<MachineQrPositionEditorProps> = ({ userId, machineCode }) => {
  const { pages, formatMessage } = useI18n();
  const t = pages.deviceMgmt.qrEditor;
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const mc = (machineCode || '').trim().toUpperCase();
  const endpoint = userId
    ? `/admin/users/${userId}/machines/${mc}/qr-positions`
    : `/machines/${mc}/qr-positions`;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [positions, setPositions] = useState<Record<string, QrPos>>({ ...LAYOUT_DEFAULTS });
  const [showQRCode, setShowQRCode] = useState(true);
  const [showQRCodeUpdatedAt, setShowQRCodeUpdatedAt] = useState<string | null>(null);
  const [layout, setLayout] = useState('2:1');
  const [frameId, setFrameId] = useState('');
  const [userFrameId, setUserFrameId] = useState('');
  const [step, setStep] = useState(1);
  const [frames, setFrames] = useState<CustomFrame[]>([]);
  const [userFrames, setUserFrames] = useState<PreviewUserFrame[]>([]);
  const [userFramesLoading, setUserFramesLoading] = useState(false);
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skipSocket = useRef(false);
  const socketRef = useRef<Socket | null>(null);
  const userFrameCache = useRef<Record<string, PreviewUserFrame[]>>({});

  const storageKey = frameId ? frameKey(frameId) : layout;
  const layoutMeta = LAYOUTS.find((l) => l.key === layout) || LAYOUTS[0];
  const current = positions[storageKey] || positions[layout] || {
    ...LAYOUT_DEFAULTS[layout],
    layout,
  };

  const actualX = current.baseX + current.offsetX;
  const actualY = current.baseY + current.offsetY;
  const selectedFrame = frames.find((f) => f._id === frameId) || null;
  const selectedUserFrame = userFrames.find((f) => f._id === userFrameId) || null;

  const framesForLayout = useMemo(
    () => frames.filter((f) => layoutForFrame(f) === layout),
    [frames, layout],
  );

  const applyPayload = useCallback((data: { positions?: Record<string, QrPos>; showQRCode?: boolean; showQRCodeUpdatedAt?: string | null }) => {
    if (data?.positions && typeof data.positions === 'object') {
      setPositions((prev) => ({ ...LAYOUT_DEFAULTS, ...prev, ...data.positions }));
    }
    if (typeof data?.showQRCode === 'boolean') setShowQRCode(data.showQRCode);
    if (data?.showQRCodeUpdatedAt) setShowQRCodeUpdatedAt(String(data.showQRCodeUpdatedAt));
  }, []);

  const load = useCallback(async () => {
    if (!mc) return;
    setLoading(true);
    try {
      const res = await api.get(endpoint);
      applyPayload(res.data || {});
    } catch (e: any) {
      setSnack({
        open: true,
        message: e?.response?.data?.message || t.loadError,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [applyPayload, endpoint, mc, t.loadError]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (userId) {
          const res = await customFrameService.getEnabledCustomFramesForUser({
            userId,
            isActive: true,
            limit: 200,
          });
          if (!cancelled) setFrames(res.frames || []);
        } else {
          const res = await customFrameService.getCustomFrames({ isActive: true, limit: 200 });
          if (!cancelled) setFrames(res.frames || []);
        }
      } catch {
        if (!cancelled) setFrames([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    let cancelled = false;
    if (!frameId) {
      setUserFrames([]);
      setUserFrameId('');
      return;
    }
    const cached = userFrameCache.current[frameId];
    if (cached) {
      setUserFrames(cached);
      setUserFrameId((prev) => (cached.some((f) => f._id === prev) ? prev : cached[0]?._id || ''));
      return;
    }
    (async () => {
      setUserFramesLoading(true);
      try {
        let items: PreviewUserFrame[] = [];
        if (userId) {
          const res = await getAdminUserCustomFrameAssets(userId, frameId, 'user_frame', 1, 200);
          items = (res.items || []).map((item) => ({
            _id: item._id,
            name: item.name,
            url: rewriteToCdn(item.frameImage?.url || ''),
          })).filter((f) => f.url);
        } else {
          const res = await userFrameService.getUserFrames({ customFrameId: frameId, isActive: true, limit: 200 });
          items = (res.userFrames || []).map((item: { _id: string; name?: string; frameImage?: { url?: string } }) => ({
            _id: item._id,
            name: item.name || '',
            url: rewriteToCdn(item.frameImage?.url || ''),
          })).filter((f: PreviewUserFrame) => f.url);
        }
        userFrameCache.current[frameId] = items;
        if (!cancelled) {
          setUserFrames(items);
          setUserFrameId(items[0]?._id || '');
        }
      } catch {
        if (!cancelled) {
          setUserFrames([]);
          setUserFrameId('');
        }
      } finally {
        if (!cancelled) setUserFramesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [frameId, userId]);

  useEffect(() => {
    if (!mc) return;
    const socket = io(SOCKET_URL, {
      withCredentials: true,
      transports: ['websocket', 'polling'],
      reconnection: true,
      path: '/socket.io',
    });
    socketRef.current = socket;
    const join = () => socket.emit('join-machine-room', mc);
    socket.on('connect', join);
    if (socket.connected) join();
    socket.on('qr-positions:changed', (data: {
      machineCode?: string;
      positions?: Record<string, QrPos>;
      showQRCode?: boolean;
      showQRCodeUpdatedAt?: string | null;
    }) => {
      const code = String(data?.machineCode || '').trim().toUpperCase();
      if (code && code !== mc) return;
      if (skipSocket.current) return;
      applyPayload(data);
    });
    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [applyPayload, mc]);

  const persist = useCallback(
    async (nextPositions: Record<string, QrPos>, key: string, nextShow: boolean, showAt: string | null) => {
      const pos = nextPositions[key];
      if (!pos) return;
      setSaving(true);
      skipSocket.current = true;
      try {
        const body: Record<string, unknown> = {
          source: userId ? 'admin' : 'web',
          positions: {
            [key]: {
              layout: pos.layout,
              baseX: pos.baseX,
              baseY: pos.baseY,
              size: pos.size,
              offsetX: pos.offsetX,
              offsetY: pos.offsetY,
              updatedAt: pos.updatedAt,
            },
          },
        };
        if (showAt) {
          body.showQRCode = nextShow;
          body.showQRCodeUpdatedAt = showAt;
        }
        const res = await api.put(endpoint, body);
        applyPayload(res.data || {});
      } catch (e: any) {
        setSnack({
          open: true,
          message: e?.response?.data?.message || t.saveError,
          severity: 'error',
        });
      } finally {
        setSaving(false);
        setTimeout(() => {
          skipSocket.current = false;
        }, 800);
      }
    },
    [applyPayload, endpoint, t.saveError, userId],
  );

  const scheduleSave = useCallback(
    (nextPositions: Record<string, QrPos>, key: string, nextShow = showQRCode, showAt: string | null = null) => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        persist(nextPositions, key, nextShow, showAt);
      }, 450);
    },
    [persist, showQRCode],
  );

  useEffect(
    () => () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    },
    [],
  );

  const touchPosition = (patch: Partial<QrPos>) => {
    const now = new Date().toISOString();
    const base = LAYOUT_DEFAULTS[layout] || LAYOUT_DEFAULTS['2:1'];
    const prev = positions[storageKey] || positions[layout] || { ...base, layout };
    let next: QrPos = { ...prev, ...patch, layout, updatedAt: now };
    if (patch.size != null && patch.offsetX == null && patch.offsetY == null) {
      const newSize = clampSize(patch.size);
      const delta = newSize - prev.size;
      next.size = newSize;
      next.offsetX = clampOffset(prev.offsetX - delta, next.baseX, newSize);
      next.offsetY = clampOffset(prev.offsetY - delta, next.baseY, newSize);
    } else {
      next.size = clampSize(next.size);
      next.offsetX = clampOffset(next.offsetX, next.baseX, next.size);
      next.offsetY = clampOffset(next.offsetY, next.baseY, next.size);
    }
    const nextPositions = { ...positions, [storageKey]: next };
    setPositions(nextPositions);
    scheduleSave(nextPositions, storageKey);
  };

  const onShowToggle = (checked: boolean) => {
    const now = new Date().toISOString();
    setShowQRCode(checked);
    setShowQRCodeUpdatedAt(now);
    scheduleSave(positions, storageKey, checked, now);
  };

  const resetCurrent = () => {
    const def = LAYOUT_DEFAULTS[layout] || LAYOUT_DEFAULTS['2:1'];
    touchPosition({
      baseX: def.baseX,
      baseY: def.baseY,
      size: 30,
      offsetX: 0,
      offsetY: 0,
    });
  };

  const longSide = isDesktop ? 320 : 240;
  const scale = longSide / Math.max(layoutMeta.w, layoutMeta.h);
  const previewW = Math.round(layoutMeta.w * scale);
  const previewH = Math.round(layoutMeta.h * scale);
  const qrPx = Math.max(12, Math.round(current.size * scale));
  const qrLeft = clamp(Math.round(actualX * scale), 0, Math.max(0, previewW - qrPx));
  const qrTop = clamp(Math.round(actualY * scale), 0, Math.max(0, previewH - qrPx));

  const selectLayout = (key: string) => {
    setLayout(key);
    setFrameId('');
    setUserFrameId('');
    setUserFrames([]);
  };

  const frameCardSx = (selected: boolean) => ({
    flex: isDesktop ? '0 0 auto' : '0 0 auto',
    width: isDesktop ? '100%' : 92,
    cursor: 'pointer',
    borderRadius: 2,
    border: '2px solid',
    borderColor: selected ? PINK : alpha(PINK, 0.22),
    bgcolor: selected ? alpha(PINK, 0.12) : '#fff',
    p: 0.75,
    boxShadow: selected ? `0 6px 16px ${alpha(PINK, 0.2)}` : 'none',
  });

  const renderFrameList = (vertical: boolean) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: vertical ? 'column' : 'row',
        gap: 1,
        overflowX: vertical ? 'hidden' : 'auto',
        overflowY: vertical ? 'auto' : 'hidden',
        maxHeight: vertical ? 520 : undefined,
        pb: vertical ? 0 : 0.5,
        pr: vertical ? 0.5 : 0,
        '&::-webkit-scrollbar': { height: 6, width: 6 },
        '&::-webkit-scrollbar-thumb': { bgcolor: alpha(PINK, 0.35), borderRadius: 99 },
      }}
    >
      <Box sx={frameCardSx(!frameId)} onClick={() => { setFrameId(''); setUserFrames([]); setUserFrameId(''); }}>
        <Box
          sx={{
            height: vertical ? 88 : 72,
            borderRadius: 1.25,
            bgcolor: alpha(PINK, 0.08),
            border: `1.5px dashed ${alpha(PINK, 0.45)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: PINK,
            fontWeight: 800,
            fontSize: 11,
            textAlign: 'center',
            px: 0.5,
          }}
        >
          {t.layoutDefault}
        </Box>
      </Box>
      {framesForLayout.map((f) => (
        <Box key={f._id} sx={frameCardSx(frameId === f._id)} onClick={() => setFrameId(f._id)}>
          <CustomFrameLayoutPreview
            width={f.width}
            height={f.height}
            size={f.size}
            photoPositions={f.photoPositions}
            maxHeight={{ xs: 72, sm: vertical ? 96 : 72 }}
          />
        </Box>
      ))}
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={36} sx={{ color: PINK }} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={800} gutterBottom sx={{ color: '#1a1a2e' }}>
        {t.title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.5 }}>
        {t.hint}
      </Typography>

      <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 1.75 }}>
        {LAYOUTS.map((item) => {
          const on = layout === item.key;
          return (
            <Button
              key={item.key}
              size="small"
              onClick={() => selectLayout(item.key)}
              sx={{
                minWidth: 52,
                px: 1.25,
                py: 0.5,
                borderRadius: 999,
                fontWeight: 800,
                fontSize: 12,
                textTransform: 'none',
                color: on ? '#fff' : PINK,
                bgcolor: on ? PINK : '#fff',
                border: `1.5px solid ${on ? PINK : alpha(PINK, 0.4)}`,
                boxShadow: on ? `0 4px 12px ${alpha(PINK, 0.28)}` : 'none',
                '&:hover': { bgcolor: on ? PINK : alpha(PINK, 0.08) },
              }}
            >
              {item.key}
            </Button>
          );
        })}
      </Stack>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        {isDesktop && (
          <Box sx={{ width: 128, flexShrink: 0 }}>
            <Typography variant="caption" fontWeight={800} sx={{ color: PINK, display: 'block', mb: 1 }}>
              {t.frame}
            </Typography>
            {renderFrameList(true)}
          </Box>
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {!isDesktop && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" fontWeight={800} sx={{ color: PINK, display: 'block', mb: 1 }}>
                {t.frame}
              </Typography>
              {renderFrameList(false)}
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
            <Box
              sx={{
                width: previewW,
                height: previewH,
                position: 'relative',
                bgcolor: '#f6f2f4',
                border: `2px solid ${alpha('#263238', 0.2)}`,
                borderRadius: 1.5,
                overflow: 'hidden',
                boxShadow: `0 10px 24px ${alpha('#000', 0.08)}`,
              }}
            >
              {selectedFrame && !selectedUserFrame && (
                <Box sx={{ position: 'absolute', inset: 8 }}>
                  <CustomFrameLayoutPreview
                    width={selectedFrame.width}
                    height={selectedFrame.height}
                    size={selectedFrame.size}
                    photoPositions={selectedFrame.photoPositions}
                    maxHeight={{ xs: previewH - 16, sm: previewH - 16 }}
                  />
                </Box>
              )}
              {selectedUserFrame && (
                <Box
                  component="img"
                  src={selectedUserFrame.url}
                  alt={selectedUserFrame.name}
                  sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }}
                />
              )}
              {showQRCode && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: qrLeft,
                    top: qrTop,
                    width: qrPx,
                    height: qrPx,
                    bgcolor: '#111',
                    backgroundImage:
                      'linear-gradient(45deg,#fff 25%,transparent 25%),linear-gradient(-45deg,#fff 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#fff 75%),linear-gradient(-45deg,transparent 75%,#fff 75%)',
                    backgroundSize: '7px 7px',
                    backgroundPosition: '0 0, 0 3.5px, 3.5px -3.5px, -3.5px 0',
                    borderRadius: 0.5,
                    boxShadow: '0 1px 4px rgba(0,0,0,0.35)',
                    zIndex: 2,
                  }}
                />
              )}
            </Box>
          </Box>

          {frameId ? (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" fontWeight={800} sx={{ color: PINK, display: 'block', mb: 1 }}>
                {t.userFrames}
                {userFramesLoading ? ' …' : ''}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 1,
                  overflowX: 'auto',
                  pb: 0.5,
                  '&::-webkit-scrollbar': { height: 6 },
                  '&::-webkit-scrollbar-thumb': { bgcolor: alpha(PINK, 0.35), borderRadius: 99 },
                }}
              >
                {userFrames.map((uf) => {
                  const on = userFrameId === uf._id;
                  return (
                    <Box
                      key={uf._id}
                      onClick={() => setUserFrameId(uf._id)}
                      sx={{
                        flex: '0 0 auto',
                        width: 76,
                        height: 76,
                        borderRadius: 1.5,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        border: '2px solid',
                        borderColor: on ? PINK : alpha(PINK, 0.22),
                        bgcolor: '#fff',
                      }}
                    >
                      <Box component="img" src={uf.url} alt={uf.name} sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ) : null}

          <FormControlLabel
            control={
              <Switch
                checked={showQRCode}
                onChange={(_, v) => onShowToggle(v)}
                sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: PINK }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: PINK } }}
              />
            }
            label={<Typography fontWeight={700} fontSize={14}>{t.showQr}</Typography>}
            sx={{ mb: 1.25 }}
          />

          {[
            { label: t.offsetX, value: actualX, dec: () => touchPosition({ offsetX: current.offsetX - step }), inc: () => touchPosition({ offsetX: current.offsetX + step }) },
            { label: t.offsetY, value: actualY, dec: () => touchPosition({ offsetY: current.offsetY - step }), inc: () => touchPosition({ offsetY: current.offsetY + step }) },
          ].map((row) => (
            <Stack key={row.label} direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
              <Typography variant="body2" sx={{ minWidth: 64, fontWeight: 800 }}>
                {row.label}
              </Typography>
              <IconButton size="small" onClick={row.dec} sx={nudgeBtnSx}>
                <RemoveIcon fontSize="small" />
              </IconButton>
              <Box
                sx={{
                  minWidth: 56,
                  textAlign: 'center',
                  fontFamily: 'monospace',
                  fontWeight: 800,
                  fontSize: 16,
                  py: 0.5,
                  px: 1,
                  borderRadius: 1.5,
                  bgcolor: alpha(PINK, 0.08),
                  color: '#1a1a2e',
                }}
              >
                {row.value}
              </Box>
              <IconButton size="small" onClick={row.inc} sx={nudgeBtnSx}>
                <AddIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            sx={{ mb: 1.5 }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={800} sx={{ mb: 0.25 }}>
                {t.size}: {current.size}
              </Typography>
              <Slider
                min={30}
                max={60}
                value={current.size}
                onChange={(_, v) => touchPosition({ size: Number(v) })}
                sx={{ color: PINK, py: 1 }}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1,
                py: 0.6,
                borderRadius: 999,
                border: `1.5px solid ${alpha(PINK, 0.35)}`,
                bgcolor: alpha(PINK, 0.06),
                alignSelf: { xs: 'flex-start', sm: 'center' },
              }}
            >
              <Typography variant="caption" fontWeight={800} sx={{ pl: 0.75, color: PINK, whiteSpace: 'nowrap' }}>
                {t.step}
              </Typography>
              <IconButton size="small" onClick={() => setStep((s) => clamp(s - 1, 1, 30))} sx={{ ...nudgeBtnSx, width: 28, height: 28 }}>
                <RemoveIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <Typography sx={{ minWidth: 22, textAlign: 'center', fontWeight: 800, fontFamily: 'monospace' }}>
                {step}
              </Typography>
              <IconButton size="small" onClick={() => setStep((s) => clamp(s + 1, 1, 30))} sx={{ ...nudgeBtnSx, width: 28, height: 28 }}>
                <AddIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={resetCurrent}
              disabled={saving}
              sx={{ borderRadius: 999, textTransform: 'none', fontWeight: 800, borderColor: alpha(PINK, 0.4), color: PINK }}
            >
              {t.reset}
            </Button>
            {saving ? <CircularProgress size={16} sx={{ color: PINK }} /> : null}
          </Stack>

          <Typography variant="caption" color="text.secondary">
            {formatMessage(t.lastEdited, {
              date: current.updatedAt ? new Date(current.updatedAt).toLocaleString() : '—',
            })}
            {showQRCodeUpdatedAt ? ` · ${new Date(showQRCodeUpdatedAt).toLocaleString()}` : ''}
          </Typography>
        </Box>
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={2500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} variant="filled" onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MachineQrPositionEditor;
